import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { LABEL_SIZE_LABELS, type BinUnitType, type GradeKind, type HarvestSettings, type LabelSize, type PackoutGrade } from '../../types/harvest';
import { Notice } from '../labour/shared';

const GRADE_KIND: Record<GradeKind, string> = { saleable: 'Packed / sold', processing: 'Processing', waste: 'Waste' };

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Label size, season start and bin types. Editing needs Farm Jobs Admin. */
export default function HarvestSettingsPanel({ settings, canEdit, onChanged }: {
  settings: HarvestSettings | null; canEdit: boolean; onChanged: () => void;
}) {
  const [form, setForm] = useState<HarvestSettings | null>(settings);
  const [types, setTypes] = useState<BinUnitType[]>([]);
  const [grades, setGrades] = useState<PackoutGrade[]>([]);
  const [addingGrade, setAddingGrade] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setForm(settings); }, [settings]);
  const loadTypes = useCallback(() => {
    harvestService.getUnitTypes(true).then(setTypes).catch(e => setError(errorMessage(e, 'Could not load bin types')));
  }, []);
  useEffect(() => { loadTypes(); }, [loadTypes]);
  const loadGrades = useCallback(() => {
    harvestService.getGrades(true).then(setGrades).catch(e => setError(errorMessage(e, 'Could not load grades')));
  }, []);
  useEffect(() => { loadGrades(); }, [loadGrades]);

  const save = async () => {
    if (!form) return;
    setSaved(false);
    try {
      await harvestService.saveSettings({
        label_size: form.label_size, season_start_month: form.season_start_month, default_unit_type_id: form.default_unit_type_id,
        record_actual_windows: form.record_actual_windows, share_harvest_dates: form.share_harvest_dates,
      });
      setSaved(true);
      onChanged();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    }
  };

  return (
    <div className="space-y-6">
      {error && <Notice tone="error">{error}</Notice>}
      {form && (
        <section className="space-y-3">
          <h3 className="font-semibold text-neutral-900">Labels & season</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
            <Field label="Label size" htmlFor="hs-size" hint="Print on thermal-transfer synthetic stock if bins go into cold storage.">
              <select id="hs-size" className={inputClass} value={form.label_size} disabled={!canEdit}
                onChange={e => setForm({ ...form, label_size: e.target.value as LabelSize })}>
                {form.label_sizes.map(s => <option key={s} value={s}>{LABEL_SIZE_LABELS[s]}</option>)}
              </select>
            </Field>
            <Field label="Season starts in" htmlFor="hs-month" hint="Tag codes start again from 1 each season.">
              <select id="hs-month" className={inputClass} value={form.season_start_month} disabled={!canEdit}
                onChange={e => setForm({ ...form, season_start_month: Number(e.target.value) })}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </Field>
            <Field label="Usual bin type" htmlFor="hs-type">
              <select id="hs-type" className={inputClass} value={form.default_unit_type_id ?? ''} disabled={!canEdit}
                onChange={e => setForm({ ...form, default_unit_type_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Bin</option>
                {types.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
          </div>
          <div className="space-y-2 max-w-3xl">
            <label className="flex items-start gap-2 text-sm text-neutral-700">
              <input type="checkbox" className="mt-1" disabled={!canEdit} checked={form.record_actual_windows ?? true}
                onChange={e => setForm({ ...form, record_actual_windows: e.target.checked })} />
              <span>
                <strong>Record actual harvest windows from bins.</strong> Each night the first and last bin picked in a cultivar
                area become its harvest window for that season. An estimate is replaced; a window someone confirmed or typed in is
                never changed — the difference is shown in Insights → Harvest dates.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-neutral-700">
              <input type="checkbox" className="mt-1" disabled={!canEdit} checked={form.share_harvest_dates ?? false}
                onChange={e => setForm({ ...form, share_harvest_dates: e.target.checked })} />
              <span>
                <strong>Share our harvest dates with cultivar maturity.</strong> Adds each cultivar area’s first and last pick date
                to the cultivar’s maturity by growing region, which other organisations using the cultivar register can see. No
                farm, block, bin or yield figures are shared. Off by default; turning it off takes our dates back out.
              </span>
            </label>
          </div>
          {canEdit && (
            <div className="flex items-center gap-3">
              <Button size="sm" variant="primary" onClick={save}>Save</Button>
              {saved && <span className="text-sm text-green-700">Saved</span>}
            </div>
          )}
        </section>
      )}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-neutral-900">Bin types</h3>
            <p className="text-sm text-neutral-500">The nominal weight gives estimated tonnes until the packhouse report arrives.</p>
          </div>
          {canEdit && <Button size="sm" variant="primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> New</Button>}
        </div>
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Bin types">
          {types.map(t => (
            <li key={t.id} className="px-3 py-2 text-sm flex items-center gap-2">
              <span className="font-medium text-neutral-900">{t.name}</span>
              <span className="text-neutral-500">{t.nominal_net_kg != null ? `${t.nominal_net_kg} kg` : 'no weight set'}</span>
              {t.is_builtin && <span className="text-xs text-neutral-400">built-in</span>}
              {!t.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Retired</span>}
              {!t.is_builtin && canEdit && (
                <button type="button" className="ml-auto text-xs text-indigo-600 hover:underline"
                  onClick={() => harvestService.saveUnitType(t.id, { active: !t.active }).then(loadTypes)}>
                  {t.active ? 'Retire' : 'Restore'}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-semibold text-neutral-900">Packout grades</h3>
            <p className="text-sm text-neutral-500">The grades you enter packout kg against.</p>
          </div>
          {canEdit && <Button size="sm" variant="primary" onClick={() => setAddingGrade(true)}><Plus className="h-4 w-4" /> New</Button>}
        </div>
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Packout grades">
          {grades.map(g => (
            <li key={g.id} className="px-3 py-2 text-sm flex items-center gap-2">
              <span className="font-medium text-neutral-900">{g.name}</span>
              <span className="text-neutral-500">{GRADE_KIND[g.kind]}</span>
              {g.is_builtin && <span className="text-xs text-neutral-400">built-in</span>}
              {!g.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Retired</span>}
              {!g.is_builtin && canEdit && (
                <button type="button" className="ml-auto text-xs text-indigo-600 hover:underline"
                  onClick={() => harvestService.saveGrade(g.id, { active: !g.active }).then(loadGrades)}>
                  {g.active ? 'Retire' : 'Restore'}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      {adding && <NewTypeModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); loadTypes(); }} />}
      {addingGrade && <NewGradeModal onClose={() => setAddingGrade(false)} onSaved={() => { setAddingGrade(false); loadGrades(); }} />}
    </div>
  );
}

function NewTypeModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [kg, setKg] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    try {
      await harvestService.saveUnitType(null, { name: name.trim(), nominal_net_kg: kg ? Number(kg) : null });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not add'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormModal title="New bin type" onClose={onClose} onSubmit={submit} submitting={saving} submitDisabled={!name.trim()} error={error}>
      <Field label="Name" htmlFor="nt-name"><input id="nt-name" className={inputClass} value={name} onChange={e => setName(e.target.value)} autoFocus /></Field>
      <Field label="Usual net weight (kg)" htmlFor="nt-kg"><input id="nt-kg" type="number" inputMode="decimal" className={inputClass} value={kg} onChange={e => setKg(e.target.value)} /></Field>
    </FormModal>
  );
}

function NewGradeModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<GradeKind>('saleable');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setSaving(true);
    try {
      await harvestService.saveGrade(null, { name: name.trim(), kind });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not add'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormModal title="New packout grade" onClose={onClose} onSubmit={submit} submitting={saving} submitDisabled={!name.trim()} error={error}>
      <Field label="Name" htmlFor="ng-name"><input id="ng-name" className={inputClass} value={name} onChange={e => setName(e.target.value)} autoFocus /></Field>
      <Field label="Kind" htmlFor="ng-kind">
        <select id="ng-kind" className={inputClass} value={kind} onChange={e => setKind(e.target.value as GradeKind)}>
          {(Object.keys(GRADE_KIND) as GradeKind[]).map(k => <option key={k} value={k}>{GRADE_KIND[k]}</option>)}
        </select>
      </Field>
    </FormModal>
  );
}
